import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Marker } from 'react-map-gl';
import { useSelector } from 'react-redux';

import MapPopup, { IProps as IMapPopupProps } from '@/components/route/search/MapPopup/MapPopup';
import MapMarker, { MapMarkerTypes } from '@/components/route/search/SearchMapMarker/MapMarker';
import { useSearchResultsCtx } from '@/components/route/search/SearchResults/SearchResults.context';
import { useWishlistResultsCtx } from '@/components/wishlists/WishlistDetails/WishlistResults.context';
import { TCurrency } from '@/config/locales';
import { getFlexibleDateRangeSelected } from '@/redux/selectors/queryParams';
import { IMapMarkerData } from '@/redux/selectors/search/map';
import { trackListingSelectedEvent } from '@/services/analytics/listings';
import { EListingSource } from '@/services/analytics/listings/types';
import { TFlexibleDateRange } from '@/services/analytics/search/types';
import { formatCurrency } from '@/utility/currency';
import {
  isCampgroundTile,
  ISearchResultTile,
  mapCampgroundTileToRentalPrice,
} from '@/utility/mapSearchResultToTile';
import { roundRatingNumber } from '@/utility/reviews';

type TMapPopupListingsProp = IMapPopupProps['listings'];

interface IUseMapPopupPositionArg {
  anchor: Element | null | undefined;
  popup: Element | null | undefined;
  container: Element | null | undefined;
  verticalOffset?: number;
  isVisible: boolean;
}

const getMapPopupBaseStyle = (hasPosition: boolean): React.CSSProperties => ({
  position: 'absolute',
  visibility: hasPosition ? 'visible' : 'hidden',
});

/**
 * This hook is responsible for the calculation of the placement of the map popup.
 *
 * Before we begin it's important to note that the placement calculation only happens at the
 * moment that the trigger to show the popup fires. This means that we won't try to reposition
 * the popup after the user interacts with the map.
 *
 * We need to have the refs for the map container, marker and popup in order to do that.
 * Having that, after we receive that `isVisible=true` we can begin the work of calculating
 * the appropriate position of the popup and this works in 2 steps.
 *
 * Step 1:
 *   Allow the popup to be displayed with `visibility: hidden` so we can get the ref for the
 *   popup element and then get the bounds of the popup.
 *
 * Step 2:
 *   After getting the bounds of all the elements we can calculate if the position of the marker
 *   relative to the container makes it possible to place the popup in its default[1] position,
 *   if not, we try to accomodate depending on the available space. Once we have the position
 *   calculated we can actually show the popup by using `visibility: visible`.
 *
 * The result of the calculation that makes the proper position possible is a `style` object
 * that must be applied to the popup element.
 *
 * And, in the end, when the popup is hidden we reset everything to its initial state to make
 * it easy for all the calculation process to happen when the popup must be shown again.
 *
 * [1] The default position of the popup is above of the marker and horizontally centered.
 */
const useMapPopupPosition = ({
  anchor,
  popup,
  container,
  verticalOffset = 8,
  isVisible,
}: IUseMapPopupPositionArg): React.CSSProperties => {
  const [hasPosition, setHasPosition] = useState(false);
  const [style, setStyle] = useState<React.CSSProperties>(getMapPopupBaseStyle(hasPosition));

  useEffect(() => {
    if (!isVisible || !popup || !anchor || !container) {
      return;
    }

    const popupRect = popup.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const spaceTop = anchorRect.top - containerRect.top;
    const spaceLeft = anchorRect.right - containerRect.left;
    const spaceRight = containerRect.right - anchorRect.left;
    const remainingSpaceLeft = spaceLeft - popupRect.width;
    const remainingSpaceRight = spaceRight - popupRect.width;
    const popupHalfWidthCentered = (popupRect.width - anchorRect.width) / 2;

    const hasSpaceOnTop = spaceTop >= popupRect.height + verticalOffset;
    const hasSpaceOnX =
      popupHalfWidthCentered <= spaceLeft - anchorRect.width &&
      popupHalfWidthCentered <= spaceRight - anchorRect.width;

    setStyle({
      ...getMapPopupBaseStyle(true),
      [hasSpaceOnTop ? 'bottom' : 'top']: anchorRect.height + verticalOffset,
      left: hasSpaceOnX
        ? '50%'
        : remainingSpaceRight > remainingSpaceLeft
          ? containerRect.left - anchorRect.left
          : undefined,
      right:
        !hasSpaceOnX && remainingSpaceLeft > remainingSpaceRight
          ? anchorRect.right - containerRect.right
          : undefined,
      transform: hasSpaceOnX ? 'translateX(-50%)' : undefined,
    });
    setHasPosition(true);
  }, [isVisible, popup, anchor, container, verticalOffset]);

  useEffect(() => {
    if (!isVisible) {
      setStyle(getMapPopupBaseStyle(false));
      setHasPosition(false);
    }
  }, [isVisible]);

  return style;
};

const mapListingToPopup = (
  rental: ISearchResultTile,
  index: number,
  flexibleDateRangeSelected: TFlexibleDateRange,
  promoSlug?: string,
): TMapPopupListingsProp[number] | null => {
  const { title, linkUrl, photoUrl } = rental;
  const isCampground = isCampgroundTile(rental);

  if (!title || !linkUrl || !photoUrl || !rental.details || !rental.price) {
    return null;
  }

  const year = (isCampground ? 0 : rental.details.year) || '';
  const classText = (isCampground ? '' : rental.details.classText) || '';
  const length = (isCampground ? '' : rental.details.feet) || '';
  // enforce single decimal digit and hide decimal if a whole number
  const fixedRating = rental.details.ratingNumber
    ? roundRatingNumber(rental.details.ratingNumber)
    : undefined;
  const rating =
    fixedRating && rental.details.ratingQtd
      ? { value: fixedRating, quantity: rental.details.ratingQtd }
      : undefined;
  const handleClick = () => {
    trackListingSelectedEvent({
      rental,
      eventSource: promoSlug ? EListingSource.PROMO : EListingSource.MAP_PREVIEW,
      listingPlacement: index + 1,
      flexibleDateRange: flexibleDateRangeSelected,
      promoSource: promoSlug || null,
    });
  };
  const infos = (isCampground ? [rental.sitesDisplayText || ''] : rental.details.infos) || [];
  const price = isCampground ? mapCampgroundTileToRentalPrice(rental) : rental.price;

  return {
    id: rental.id,
    title,
    price,
    href: linkUrl,
    image: {
      alt: '',
      // @ts-expect-error fixable: unchecked index access
      src: Array<string>().concat(photoUrl)[0],
    },
    year,
    classText,
    length,
    rating,
    infos,
    isInstantBook: isCampground ? false : !!rental.instantBook,
    isUnavailable: isCampground ? false : !!rental.unavailable,
    isSuperhost: isCampground
      ? false
      : !!rental.details.tags?.some(tag => tag.slug === 'superhost'),
    onClick: handleClick,
    isStay: isCampground ? false : !!rental.isStay,
    isCampground,
    isFavorite: rental.isFavorite,
  };
};

const preventScrollOnParent = (e: React.WheelEvent): void => {
  e.stopPropagation();

  const { clientHeight, scrollTop, scrollHeight } = e.currentTarget;
  const shouldStopScroll =
    e.deltaY > 0 ? clientHeight + scrollTop === scrollHeight : scrollTop === 0;

  if (shouldStopScroll) {
    e.preventDefault();
  }
};

type TProps = IMapMarkerData & {
  isPopupVisible: boolean;
  container: Element | null;
  currency: TCurrency;
  onClick?: (id: number) => void;
  promoSlug?: string;
  isOnWishlist?: boolean;
};

const SearchMapMarker: React.FC<TProps> = ({
  isPopupVisible,
  container,
  currency,
  lat,
  lon,
  rentals,
  onClick,
  promoSlug,
  isOnWishlist,
}) => {
  const markerRef = useRef<HTMLDivElement>(null);
  // We use `useState` here because we want to force a rerender when we get the popup ref
  const [popupRef, setPopupRef] = useState<HTMLDivElement | null>(null);
  const popupStyle = useMapPopupPosition({
    container,
    anchor: markerRef.current,
    popup: popupRef,
    isVisible: isPopupVisible,
  });

  const { hoveredId: searchHoveredId, updateMapHoveredId: updateSearchMapHoveredId } =
    useSearchResultsCtx();
  const { hoveredId: wishlistHoveredId, updateMapHoveredId: updateWishlistHoveredId } =
    useWishlistResultsCtx();
  const flexibleDateRangeSelected = useSelector(getFlexibleDateRangeSelected);

  const rentalId = rentals[0]?.id || 0;
  const isHovered = isOnWishlist
    ? rentals.some(r => wishlistHoveredId === r.id)
    : rentals.some(r => searchHoveredId === r.id);

  const handleOnClick = useCallback<(e: React.MouseEvent<HTMLButtonElement>) => void>(
    e => {
      e.stopPropagation();
      onClick?.(rentalId);
    },
    [rentalId, onClick],
  );

  const handleOnMouseEnter = () => {
    if (isOnWishlist) {
      updateWishlistHoveredId(rentalId);
    } else {
      updateSearchMapHoveredId(rentalId);
    }
  };

  const handleOnMouseLeave = () => {
    if (isOnWishlist) {
      updateWishlistHoveredId(null);
    } else {
      updateSearchMapHoveredId(null);
    }
  };

  // Prevent touch scroll on popup to trigger scroll on body
  const previousBodyOverflow = useRef<string | null>(null);
  const handleTouchStart = useCallback(() => {
    previousBodyOverflow.current = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
  }, []);
  const handleTouchEnd = useCallback(() => {
    if (typeof previousBodyOverflow.current === 'string') {
      window.document.body.style.overflow = previousBodyOverflow.current;
      previousBodyOverflow.current = null;
    }
  }, []);

  const listings = rentals
    .map((rental, idx) => mapListingToPopup(rental, idx, flexibleDateRangeSelected, promoSlug))
    .filter(Boolean) as TMapPopupListingsProp;
  const listingsCount = listings.length;

  if (!listingsCount) {
    return null;
  }

  const isStay = listings.some(rental => rental.isStay);

  const isGroup = listingsCount > 1;
  // @ts-expect-error fixable: unchecked index access
  const [{ isInstantBook, isUnavailable, price }] = listings;

  return (
    <Marker style={{ zIndex: isPopupVisible || isHovered ? 10 : 0 }} latitude={lat} longitude={lon}>
      <div className="relative" ref={markerRef}>
        {isPopupVisible && (
          <MapPopup
            ref={setPopupRef}
            style={popupStyle}
            currency={currency}
            listings={listings}
            onWheelCapture={preventScrollOnParent}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            isOnWishlist={isOnWishlist}
          />
        )}

        <MapMarker
          type={isGroup ? MapMarkerTypes.GROUP : MapMarkerTypes.SINGLE}
          isInstantBook={isGroup ? false : isInstantBook}
          isUnavailable={isGroup ? false : isUnavailable}
          isStay={isStay}
          isActive={isPopupVisible || isHovered}
          onClick={handleOnClick}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
          isOnWishlist={isOnWishlist}>
          {isGroup
            ? listingsCount
            : formatCurrency({
                currency,
                digits: 0,
                maxDigits: 0,
                priceInCents: price.currentPrice || price.originalPrice,
              })}
        </MapMarker>
      </div>
    </Marker>
  );
};

export default React.memo(SearchMapMarker);
